import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Accessibility, Users, Timer, Sparkles, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const HERO = "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/dncda7pf_Holiday%20Improvaneers-069.jpg";
const IMPACT_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/c7zzxfrr_Improvaneers%204%20Beyond%20Labels%202024.webp";
const GALLERY = [
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/fsofp8qx_Cleveland%202.webp", alt: "A director hosts on a dark stage while performers act out an improv scene" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/vhf9u3wu_IMG_4889.jpg", alt: "Participants and peer mentors playing an energetic warm-up game together" },
  { src: "https://customer-assets-agu9un31.emergentagent.net/job_improv-prompt-engine/artifacts/zdc3iqkc_Holiday%20Improvaneers-Medina%201.jpg", alt: "The Improvaneers troupe performing together on stage in matching shirts" },
];

const TESTIMONIALS = [
  { quote: "AMAZING program and so much fun. We have noticed a new level of confidence in our son. His sense of humor is shining through.", who: "Caregiver of a live participant" },
  { quote: "She feels seen, heard, and learned that she can do this independently. It improves her speech and builds confidence — an evening of smiles, giggles and happy wiggles.", who: "Izzy & Darlene" },
  { quote: "This program is FANTASTIC. It gives our kids the confidence to speak in a group and have fun while practicing this essential communication skill.", who: "Caregiver of a live participant" },
];

export default function Home() {
  const [games, setGames] = useState([]);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [demoBusy, setDemoBusy] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/games").then(({ data }) => setGames(data.slice(0, 4))).catch(() => {});
  }, []);

  const adminDemo = async () => {
    setDemoBusy(true);
    try {
      await login("tara.may@gmail.com", "Improv2026!");
      toast.success("Signed in as admin (demo)");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Demo login failed");
    } finally {
      setDemoBusy(false);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/contact", { name: "Newsletter", email, message: "Newsletter signup", kind: "newsletter" });
      setSubscribed(true);
      setEmail("");
    } catch {}
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#140a0c] text-white">
        <div className="absolute inset-0 stage-scrim" aria-hidden="true" />
        <img src={HERO} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#bf6163]">Imagine the possibilities</span>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-tight">
              Improv For Every Brain &amp; Every Body
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#dfd8da] max-w-2xl">
              Using improvisation to greatly increase social, workplace, and lifetime opportunities for people
              of <strong>ALL abilities</strong>. Games are fun and engaging — and built to grow real communication,
              confidence, and connection.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-[#920e12] hover:bg-[#bf6163] text-white text-base h-12 px-7" data-testid="hero-enroll-btn">
                <Link to="/shop">Enroll in a Class <ArrowRight className="ml-2" size={18} /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-7 text-base border-white/40 bg-transparent text-white hover:bg-white hover:text-[#140a0c]" data-testid="hero-story-btn">
                <Link to="/about">Our Story</Link>
              </Button>
            </div>
            <div className="mt-6">
              <button
                onClick={adminDemo}
                disabled={demoBusy}
                data-testid="admin-demo-btn"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#ffdedf] bg-white/10 hover:bg-white/20 border border-dashed border-[#bf6163] rounded-full px-4 py-2 transition-colors duration-150 disabled:opacity-50"
              >
                <ShieldCheck size={16} aria-hidden="true" />
                {demoBusy ? "Signing in…" : "Admin demo (testers) — view as admin, no login"}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Accessibility, title: "ALL abilities welcome", body: "Fully inclusive classes, ages 16 and up. Every game has a way in for every participant — no one is ever forced to do something they're not ready for." },
            { icon: Users, title: "Skills that carry over", body: "Each game is geared toward key skills — listening, problem-solving, teamwork, and communication that opens social and workplace doors." },
            { icon: Timer, title: "A method you can run", body: "Directors open an agenda and run it live — timed blocks, projector view, and a prompt generator, ending in a showcase of real growth." },
          ].map((p) => (
            <div key={p.title} className="bg-white rounded-2xl p-7 border border-[#dfd8da] shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-[#920e12] text-white flex items-center justify-center">
                <p.icon size={22} aria-hidden="true" />
              </div>
              <h2 className="mt-5 text-xl font-bold text-[#1a1617]">{p.title}</h2>
              <p className="mt-2 text-sm sm:text-base leading-relaxed text-[#545454]">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mission quote */}
      <section className="bg-[#221517]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 text-center">
          <p className="font-display text-xl sm:text-2xl lg:text-3xl leading-snug text-[#ffdedf]">
            "No one who participates will ever be forced to do something they aren't ready to do. We work to
            understand each individual's abilities and thresholds of discovery — to provide the best experience possible."
          </p>
          <p className="mt-5 text-sm uppercase tracking-[0.2em] font-extrabold text-[#bf6163]">Rob Snow · CEO &amp; Creator</p>
        </div>
      </section>

      {/* Games teaser */}
      <section className="bg-white border-y border-[#dfd8da]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">The Library</span>
              <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617]">Games we play</h2>
            </div>
            <Button asChild variant="outline" className="border-[#920e12] text-[#920e12]" data-testid="home-view-games">
              <Link to="/games">Browse all games <ArrowRight className="ml-2" size={16} /></Link>
            </Button>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {games.map((g) => (
              <Link
                key={g.id}
                to={`/games/${g.id}`}
                data-testid={`home-game-${g.id}`}
                className="group rounded-2xl border border-[#dfd8da] p-5 hover:border-[#920e12] transition-colors duration-150 bg-[#f9f6f6]"
              >
                <div className="text-[10px] uppercase tracking-widest font-extrabold text-[#bf6163]">Level {g.level}</div>
                <h3 className="mt-1 text-lg font-bold text-[#1a1617] group-hover:text-[#920e12]">{g.name}</h3>
                <p className="mt-2 text-sm text-[#545454]">{g.blurb}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {g.skill_sets.slice(0, 2).map((s) => (
                    <span key={s} className="text-[11px] px-2 py-0.5 rounded-full bg-[#ffdedf] text-[#920e12] font-semibold">{s}</span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Impact + testimonials */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="rounded-3xl overflow-hidden border border-[#dfd8da] shadow-lg">
          <img src={IMPACT_IMG} alt="Inclusive classroom where every voice and expression is celebrated" className="w-full h-full object-cover aspect-[4/3]" />
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">In their words</span>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617]">Confidence you can see</h2>
          <div className="mt-6 space-y-5">
            {TESTIMONIALS.map((t, i) => (
              <blockquote key={i} className="border-l-4 border-[#bf6163] pl-4 py-1" data-testid={`testimonial-${i}`}>
                <p className="text-base sm:text-lg text-[#1a1617] italic">“{t.quote}”</p>
                <cite className="mt-1 block text-sm not-italic font-semibold text-[#545454]">— {t.who}</cite>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* Photo gallery */}
      <section className="bg-[#f9f6f6] border-y border-[#dfd8da]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="text-xs uppercase tracking-[0.22em] font-extrabold text-[#920e12]">Moments</span>
          <h2 className="mt-2 font-display text-3xl lg:text-4xl text-[#1a1617]">From our stages &amp; classrooms</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="home-gallery">
            {GALLERY.map((g, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-[#dfd8da] shadow-sm">
                <img src={g.src} alt={g.alt} loading="lazy" className="w-full h-full object-cover aspect-[4/3] hover:scale-[1.03] transition-transform duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[#550001] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center">
          <Sparkles className="mx-auto text-[#bf6163]" aria-hidden="true" />
          <h2 className="mt-4 font-display text-2xl sm:text-3xl">Stay in the loop</h2>
          <p className="mt-2 text-[#dfd8da]">Class openings, workshops, and the occasional play prompt. No spam.</p>
          {subscribed ? (
            <p className="mt-6 text-lg font-semibold text-[#ffdedf]" data-testid="newsletter-success">You're on the list — welcome aboard! 🎭</p>
          ) : (
            <form onSubmit={subscribe} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <label htmlFor="nl-email" className="sr-only">Email address</label>
              <input
                id="nl-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="newsletter-email"
                className="flex-1 h-12 px-4 rounded-lg text-[#1a1617] border-2 border-transparent focus:border-[#bf6163]"
              />
              <Button type="submit" className="h-12 bg-white text-[#550001] hover:bg-[#ffdedf] font-bold" data-testid="newsletter-submit">
                Subscribe
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
